import { useRef, useState } from "react"
import { ArrowLeftRight, Download } from "lucide-react"
import { useSearchParams } from "react-router-dom"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { LoadingState } from "@/app/components/LoadingState"
import { PageHeader } from "@/app/components/PageHeader"
import { secondaryButton } from "@/app/lib/buttonStyles"
import { TransactionsTable } from "./TransactionsTable"
import { useTransactionsView } from "./useTransactionsView"

export function TransactionsPage() {
  const { theme } = useClientTheme()
  const { canWrite } = useCurrentUser()
  const [showDeleted, setShowDeleted] = useState(false)
  const [params] = useSearchParams()
  const tx = params.get("tx") ?? undefined
  // The table owns the current view (active tab + filters + sort); it parks
  // its export fn here so the header button can trigger it (mirrors
  // InventoryPage's Export CSV wiring).
  const exportRef = useRef<(() => void) | null>(null)

  const includeDeleted = canWrite && showDeleted
  const view = useTransactionsView({ initialSearch: tx, includeDeleted })

  const exportDisabled = !canWrite || view.meta.total_count === 0 || view.isExporting

  // Desktop is viewport-fit: the Shell's <main> is a definite-height scroll
  // area (h-screen flex chain), so h-full + flex columns let the table absorb
  // exactly the leftover space and keep the KPI/pagination footer always on
  // screen — no page scroll, no magic vh numbers. Mobile keeps natural
  // document flow (the card list wants the page to scroll).
  return (
    <div className="flex h-full min-h-0 flex-col p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-none items-center justify-between gap-3 flex-wrap">
          <PageHeader
            title="Transaction History"
            subtitle="Leasehold & mineral conveyances — acquisitions & divestitures"
            theme={theme}
            icon={<ArrowLeftRight size={17} strokeWidth={1.75} />}
          />
          {/* mb-4 matches PageHeader's own bottom margin so the row aligns.
              Viewers get read-only access — export is owners/editors/staff. */}
          <button
            type="button"
            onClick={() => exportRef.current?.()}
            disabled={exportDisabled}
            className="btn mb-4"
            style={{
              ...secondaryButton(theme),
              opacity: exportDisabled ? 0.6 : 1,
              cursor: exportDisabled ? "default" : "pointer",
            }}
            title={
              !canWrite
                ? "Viewers have read-only access"
                : view.isExporting
                  ? "Export in progress…"
                  : view.meta.total_count === 0
                    ? "No rows to export"
                    : "Export current view to CSV"
            }
          >
            <Download size={14} /> {view.isExporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
        {view.isLoading ? (
          <LoadingState theme={theme} />
        ) : (
          <TransactionsTable
            rows={view.rows}
            meta={view.meta}
            summary={view.summary}
            canWrite={canWrite}
            showDeleted={showDeleted}
            onToggleDeleted={setShowDeleted}
            kind={view.kind}
            onKindChange={view.setKind}
            q={view.q}
            onQChange={view.setQ}
            sectionSel={view.sectionSel}
            onSectionChange={view.setSectionSel}
            trSel={view.trSel}
            onTrChange={view.setTrSel}
            typeSel={view.typeSel}
            onTypeChange={view.setTypeSel}
            interestSel={view.interestSel}
            onInterestChange={view.setInterestSel}
            hasOrriSel={view.hasOrriSel}
            onHasOrriChange={view.setHasOrriSel}
            hasFilters={view.hasFilters}
            onClearFilters={view.clearFilters}
            options={view.options}
            sortKey={view.sortKey}
            sortDir={view.sortDir}
            onSort={view.handleSort}
            page={view.page}
            perPage={view.perPage}
            onPageChange={view.setPage}
            onPerPageChange={view.setPerPage}
            isFetching={view.isFetching}
            onExport={view.exportCsv}
            exportRef={exportRef}
          />
        )}
      </div>
    </div>
  )
}
